import { ChangeDetectorRef, Component, ViewChildren, QueryList } from "@angular/core";
import * as Immutable from "immutable";
import { ListItemModel, ListModel, ListComponent } from "../../../shared/components/list";
import { VariablePanel, ConfVariableValue, VariableControl, GroupConfUIItem, StoredVariableValue } from "../../../shared/models";
import { ProductDataStore } from "../../../shared/providers/productData";
import { ConfiguratorStore, ConfPageSessionService } from "../../providers";
import { ListModelCacheService } from "../providers/listModelCacheService";
import { BaseDetailItemComponent } from "../shared/baseDetailItemComponent";
import { ListRowModel } from "../shared/listRowModel";
import { PopupService, PopperConfig, PopoverService } from "../../../../shared/components";
import { GlobalDataStore } from "../../../shared/providers/globalData";

@Component({
    template: ''
})
export class AbstractVariablesDetailComponent extends BaseDetailItemComponent {

  public variablesValues: Array<ConfVariableValue> = [];
  public storedVariablesValues: Array<StoredVariableValue> = [];
  protected row = new ListRowModel();
  public detailTitle: string = this.strings.Variables;

  @ViewChildren(ListComponent)
  public criteriaComponents: QueryList<ListComponent>;

  constructor(
    public cdr: ChangeDetectorRef,
    public confStore: ConfiguratorStore,
    public confPageSessionService: ConfPageSessionService,
    public productStore: ProductDataStore,
    public modelsService: ListModelCacheService,
    public popupService: PopupService,
    public globalDataStore: GlobalDataStore,
    public popoverService: PopoverService
  ) {
    super(confPageSessionService);
  }

  onDataReady(): void {
    // overriden
  }

  init(): void {

    let conf = this.confPageSessionService.activeConfiguration;
    let values = conf.variableValues;

    if (!values) {
      this.visible = false;
      return;
    }

    this.rows = this.rows.clear();
    this.row.cells = [];

    let visibleVariableItemById: Map<number, VariableControl> = new Map<number, VariableControl>();

    let variableControls = Immutable.List<VariableControl>();

    let setControlByItem = (varPanelId: number) => {

      let varDec: VariablePanel = this.productStore.getEntity(varPanelId);
      if (!varDec)
        return;

      variableControls = varDec.items;

      varDec.items.forEach(x => {

        visibleVariableItemById.set(x.variableId, x);

      });

    }

    if (this.confUIItem) {

      // Grouped variable panels.
      if (this.confUIItem instanceof GroupConfUIItem) {

        this.detailTitle = this.globalDataStore.getGlobalData().globalSettings.showCurrentOrStoredVariables.startsWith("Current") ? this.strings.CalculatedData : this.strings.Variables;

        // Panels are available at items level. Loop through them and get all panel items
        this.confUIItem.items.forEach(y => {
          setControlByItem(y.id);
        });

      }

      // Ungrouped variable panels.
      else {

        this.detailTitle = this.strings.Variables;

        setControlByItem(this.confUIItem.id);

        let variablePanel: VariablePanel = this.productStore.getEntity<VariablePanel>(this.confUIItem.id);
        if (variablePanel && variablePanel.title && variablePanel.title != "")
          this.detailTitle = variablePanel.title;
      }
    }

    variableControls.forEach(panelItem => {
      let id = panelItem.variableId;
      if (!values.contains(id) || !visibleVariableItemById.has(id))
        return;

      // Add into cells.
      let listModel = this.getVariablesDetail(id, visibleVariableItemById);

      if (listModel)
        this.row.cells.push(listModel);
      else
        this.visible = false;
    });

    // Add empty cell to complete the border
    if (this.shouldAddEmptyCell(this.row.cells.length)) {
      this.row.cells.push(new ListModel());
    }

    this.visible = this.row.cells.length > 0;
    this.rows = this.rows.push(this.row);
  }

  getVariablesDetail(id: number, panelIdById: Map<number, VariableControl>, isPopup = false): ListModel {

    let conf = this.confPageSessionService.activeConfiguration;
    let varValue = this.confStore.getConfValue(conf.longId, this.confPageSessionService.confSessionId, id);

    // Make it visible If it contains any variables.
    if (!(varValue instanceof ConfVariableValue)) {
      return;
    }

    // Create list model, set variables detail
    let list: ListModel = this.modelsService.getCachedIfValueEqual(id.toString(), varValue);
    if (list) {

      // Add into cells.
      return list;

    } else list = new ListModel();

    list = list.setTitle(varValue.title);
    list = list.setIcon(`${varValue.valueType.toLowerCase()}param`);
    list = list.setBackgroundImage("variablepanel");
    list = list.setEmptyValueText("<No Value>"); // TODO: Localize it.
    list = list.setIsPrintView(this.isPrintView);
    list = list.setId(varValue.longId.toString());


    // Set conf value.
    let itemModel: ListItemModel = new ListItemModel();
    let storedItemModel: ListItemModel = new ListItemModel();

    if (panelIdById && panelIdById.has(varValue.longId)) {

      let panelItem: VariableControl = panelIdById.get(varValue.longId);

      // TODO: Used in modelsservice for caching???
      list = list.setTag(panelItem);
    }

    let value: string;
    let unit: string;

    // If the stored value should be shown first, the calculated value in a popup
    let storedFirst = this.globalDataStore.getGlobalData().globalSettings.showCurrentOrStoredVariables.startsWith("Stored");

    // Create a stored value list item
    if (storedFirst || isPopup) {
      if (conf.storedData.variableValues) {
        let storedVarVal = conf.storedData.variableValues.find(x => x.longId == id);
        if (storedVarVal) {
          value = storedVarVal.value;

          unit = isPopup && storedVarVal.value ? `${this.nullAsEmptyStringOrAddSpace(storedVarVal.unit)}<span>(${this.strings.Stored})</span>` : storedVarVal.value ? storedVarVal.unit : "";
        }
      }
      storedItemModel = this.initItemModel(value, unit, storedItemModel, !storedFirst && isPopup);
    }

    // Create a calculated value list item
    if (!storedFirst || isPopup) {
      // Read formatted value
      if (list.tag instanceof VariableControl && list.tag.numericFormat)
        value = varValue.valueByNumericFormat.get(list.tag.numericFormat);
      else
        value = varValue.value;

      unit = isPopup && varValue.value ? `${this.nullAsEmptyStringOrAddSpace(varValue.unit)}<span>(${this.strings.Calculated})</span>` : varValue.value ? varValue.unit : "";
      itemModel = this.initItemModel(value, unit, itemModel, storedFirst && isPopup);
    }

    let items: Immutable.List<ListItemModel> = list.items;

    if (!items)
      items = Immutable.List<ListItemModel>();

    if (storedFirst) {
      items = items.push(storedItemModel);
      if (isPopup)
        items = items.push(itemModel);
    }
    else {
      items = items.push(itemModel);
      if (isPopup)
        items = items.push(storedItemModel);
    }

    list = list.setItems(items);

    // Add into cells.
    return list;
  }

  nullAsEmptyStringOrAddSpace(value: string): string {
    if (value == null)
      return "";
    else
      return value + " ";
  }

  initItemModel(value: string, unit: string, itemModel: ListItemModel, isItalic = false): ListItemModel {
    const valueAndUnit = unit ? `${this.nullAsEmptyStringOrAddSpace(value)}${unit}` : value;
    itemModel = itemModel.setTitle(isItalic && valueAndUnit != null ? `<i>${valueAndUnit}</i>` : valueAndUnit);
    itemModel = itemModel.setIsPrintView(this.isPrintView);
    return itemModel;
  }

  openVariablePopup(list: ListModel): void {
    if (!this.globalDataStore.getGlobalData().globalSettings.showCurrentOrStoredVariables.endsWith("Popup"))
      return;

    let variableControl = new Map<number, VariableControl>();

    if (list.tag instanceof VariableControl)
      variableControl.set(list.tag.variableId, list.tag);

    let popupList = this.getVariablesDetail(+list.id, variableControl, true);

    let refElement = this.criteriaComponents.find(x => x.list == list)?.listDiv.nativeElement;

    if (!refElement)
      return;

    let info = <PopperConfig>{
      title: popupList.title,
      subTitle: this.globalDataStore.getGlobalData().globalSettings.showCurrentOrStoredVariables.startsWith("Current") ? this.strings.StoredData : this.strings.CalculatedData,
      open: true,
      refElement: refElement,
      tag: popupList
    };

    this.popoverService.show(info);
  }

  subscribeDataChange(): void {

    this.confStore.onConfigurationVariableValuesChange(this.configurationId, this.confSessionId, (propValues) => {

      this.init();
      this.cdr.markForCheck();

    }).unsubscribeOn(this.unsubscribeSubject);
  }

}